import { UserType } from "globals/types/models.ts";
import { useAppDispatch, useAppSelector } from "config/store";
import { useMount } from "react-use";
import { adminActions } from "config/store/modules/admin.ts";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminConnectedContainer from "components/container/AdminConnectedContainer.tsx";
import { useTranslation } from "react-i18next";
import { tryAsyncFunc } from "globals/functions/async.ts";
import { getValuesFromUser, UserFormValuesType } from "utils/form.ts";
import ajax from "config/ajax.ts";
import LoadingContainer from "components/container/LoadingContainer.tsx";
import { Form, Formik } from "formik";
import Button from "components/atom/button/Button.tsx";
import AdminUserForm from "components/layouts/form/AdminUserForm.tsx";
import ConfirmActionModal from "components/modals/ConfirmActionModal.tsx";
import FormCheckbox from "components/form/FormCheckbox.tsx";

function AdminSingleUserPage() {
  const users: UserType[] = useAppSelector(state => state.admin.users);
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(state => state.auth.user);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [user, setUser] = useState<UserType | null>(null);


  useMount(async () => {
    if (!params.userId) {
      navigate("/profile/admin/users");
      return;
    }

    const userFromArr = users.find(e => e.id.toString() === params.userId as string) || null;

    if (userFromArr) {
      setUser(userFromArr);
      return;
    }

    const [userFromReq, err] = await tryAsyncFunc(dispatch(adminActions.getPublicUserById(params.userId as string)));

    if (err) {
      navigate("/profile/admin/users");
      return;
    }

    setUser(userFromReq);
  });

  const restoreUser = useCallback(async () => {
    if (!user) return;

    const [userFromReq, err] = await tryAsyncFunc(ajax.post(`/connected/admin/users/id/${user.id}/restore`));

    if (err) {
      return;
    }

    setUser(userFromReq);
  }, [user]);

  const deleteUser = useCallback(async () => {
    if (!user) return;

    const [_, err] = await tryAsyncFunc(ajax.delete(`/connected/admin/users/id/${user.id}`, {
      params: {
        force: forceDelete
      }
    }));

    if (err) {
      return;
    }

    await dispatch(adminActions.deletedUser(user.id));
    navigate("/profile/admin/users");
  }, [dispatch, navigate, user, forceDelete]);


  const canDeleteUser = useMemo<boolean>(() => {
    if (!user) return false;
    if (!currentUser) return false;
    if (user.id === currentUser.id) return false;
    if (user.id === 1) return false; //info@betheremap.com
    return true;
  }, [user, currentUser]);

  const canRestoreUser = useMemo<boolean>(() => {
    if (!user) return false;
    if (!currentUser) return false;
    if (user.id === currentUser.id) return false;
    if (user.id === 1) return false; //info@betheremap.com
    return !!user.deletedAt;
  }, [user, currentUser]);

  if (!user) {
    return null;
  }


  return (
    <AdminConnectedContainer title={t("user.modify")}>
      <div className="w-full">
        <Formik
          initialValues={getValuesFromUser(user)}
          validateOnChange={false}
          validateOnBlur={false}
          validate={values => {
            const errors: Partial<{ [K in keyof UserFormValuesType]: string }> = {};//same keys as type but with string value
            if (!values.email) {
              errors.email = t("forms.errors.requiredEmail");
            }
            if (!values.name) {
              errors.name = t("forms.errors.requiredName");
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting, setStatus, setFieldError, setValues }) => {
            setSubmitting(true);
            setStatus(null);

            //sync with values
            if (values.sameAddress) {
              values.billingAddress = values.address;
              values.billingCity = values.city;
              values.billingZipcode = values.zipcode;
              values.billingState = values.state;
              values.billingCountry = values.country;
            }

            const [res, err] = await tryAsyncFunc(ajax.put(`/connected/admin/users/${user.id}`, values));
            setSubmitting(false);

            if (err) {
              const errData = err.response.data || {};
              const errors = errData.errors || {};

              if (errors.global) {
                setStatus(errors.global);
                delete errors.global;
              }

              Object.keys(errors).forEach(errKey => {
                setFieldError(errKey, errors[errKey]);
              });
              return;
            }

            setUser(res.data.user);
            setValues(getValuesFromUser(res.data.user));

            dispatch(adminActions.updatedUser(res.data.user));

            //update planType with custom ajax call, make sure to update user if needed
            if (values.planType && values.planType !== user?.planType) {
              const [planRes, errPlan] = await tryAsyncFunc(ajax.put(`/connected/admin/users/${user.id}/plans`, {
                planType: values.planType
              }));
              if (errPlan) {
                const errPlanData = errPlan.response.data || {};
                const errorsPlan = errPlanData.errors || {};

                if (errorsPlan.global) {
                  setStatus(errorsPlan.global);
                  delete errorsPlan.global;
                }

                Object.keys(errorsPlan).forEach(errKey => {
                  setFieldError(errKey, errorsPlan[errKey]);
                });
                return;
              }

              //re update le user
              setUser(planRes.data.user);
              setValues(getValuesFromUser(planRes.data.user));

              dispatch(adminActions.updatedUser(planRes.data.user));
            }
          }}
        >
          {({ isSubmitting, status }) => (
            <LoadingContainer isLoading={isSubmitting} keepChildren>
              <Form>
                <AdminUserForm />

                {status && (
                  <p className="error block">{status}</p>
                )}
                <div className="mt-4 flex w-full flex-col items-center justify-center gap-4">
                  <Button className="w-full" type="submit" disabled={isSubmitting}>
                    {t("saveModifications")}
                  </Button>
                  <Button style="text-red" className="inline-block" type="button"
                          disabled={!canDeleteUser || isSubmitting}
                          onClick={() => setShowDeleteModal(true)}>
                    {t("user.delete")}
                  </Button>
                  {canRestoreUser && (
                    <Button style="text-red" className="inline-block" type="button"
                            disabled={isSubmitting}
                            onClick={restoreUser}>
                      {t("user.restore")}
                    </Button>
                  )}
                </div>
              </Form>
            </LoadingContainer>
          )}
        </Formik>

        <ConfirmActionModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onAccept={deleteUser}>
          <Fragment>
            <span>
              {t("user.confirmDelete")}<br />
            </span>
            <div className="md:w-2/3 mx-auto">
              <FormCheckbox name="force-delete"
                            forName="force-delete"
                            checkboxClassName="w-6 h-6 min-w-6"
                            onChange={(checked) => setForceDelete(checked)}
                            checked={forceDelete}
                            label={t("forms.forceDeleteUser")} />
            </div>
          </Fragment>
        </ConfirmActionModal>

      </div>
    </AdminConnectedContainer>
  );
}

export default AdminSingleUserPage;
